import { useForm } from "react-hook-form";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import SearchTable from "../../components/template/Search/SearchTable";
import { useFilter } from "../../hooks/filter/useFilter";
import { PAGE_SIZE_OPTIONS, USER_SUMMARY } from "../../utils/constants";
import { useAuth } from "../../provider/AuthProvider";
import { Toast } from "../../components/atom/Toast/Toast";
import { UsersItemSchema } from "./data/UsersItemSchema";
import TableUsers from "./TableUsers";
import { getUsers } from "../../services/User/getUsers";
import { getProvince } from "../../services/TabelasAuxiliares/getProvince";
import SelectGroup from "../../components/molecule/SelectGroup/SelectGroup";
import { getRole } from "../../services/getRole";
import { getOrganization } from "../../services/getOrganization";
import { handleUserStateChange } from "../../helpers/handleUserStateChange";
import { Option } from "../../types/General/Option";
import { userSelectFilterData } from "./data/userSelectFilterData";
import { getCountyById } from "../../services/TabelasAuxiliares/getCountyById";
import { FilterConfig } from "../../types/Components/Table/FilterConfig";
import { TableConfig } from "../../types/Components/Table/TableConfig";
import useFetchData from "../../hooks/data/useFetchData";
import { getFiltersArray } from "../../helpers/getFiltersArray";
import { updateSelectedFilterItemsName } from "../../helpers/updateSelectedFilterItemsName";
import { PaginationValues } from "../../types/General/PaginationValues";
import { PaginationConfig } from "../../types/Components/Table/PaginationConfig";
import useTableRendering from "../../hooks/table/useTableRendering";
import { Button } from "@chakra-ui/react";
import { useCustomModal } from "../../hooks/modal/useCustomModal";
import { ModalConfig } from "../../types/Components/Modal/ModalConfig";
import ModalDownloadAudit from "../Audit/component/ModalDownloadAudit";

interface FilterState {
  RoleId: Option | null;
  OrganizationId: Option | null;
  ProvinceId: Option | null;
  CountyId: Option | null;
  IsActive: Option | null;
}

interface OptionsState {
  roleOptions: Option[];
  organizationOptions: Option[];
  provinceOptions: Option[];
  countyOptions: Option[];
  activeOptions: Option[];
}

const Users: FC = () => {
  const { addToast } = Toast();
  const navigate = useNavigate();
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [errorFindingData, setErrorFindingData] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedFilterItems, setSelectedFilterItems] = useState<string>("");
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [isSearching, setIsSearching] = useState<boolean>(true);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [countyLoaded, setCountyLoaded] = useState<boolean>(false);
  const { auth } = useAuth();
  const user = auth.user;

  const [filterState, setFilterState] = useState<FilterState>({
    RoleId: null,
    OrganizationId: null,
    ProvinceId: null,
    CountyId: null,
    IsActive: null,
  });

  const [optionsState, setOptionsState] = useState<OptionsState>({
    roleOptions: [],
    organizationOptions: [],
    provinceOptions: [],
    countyOptions: [],
    activeOptions: [
      { id: "true", name: "Ativo" },
      { id: "false", name: "Inativo" },
    ],
  });

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(UsersItemSchema),
  });

  const filterNames = useMemo(
    () => [
      "RoleId",
      "OrganizationId",
      "ProvinceId",
      "CountyId",
      "IsActive",
      "NameOrEmail",
    ],
    [],
  );
  const { filters, setFilter } = useFilter({ setValue, filterNames });

  const { isLoaded: roleLoaded } = useFetchData(getRole, (data) =>
    setOptionsState((prev) => ({ ...prev, roleOptions: data })),
  );
  const { isLoaded: organizationLoaded } = useFetchData(
    getOrganization,
    (data) =>
      setOptionsState((prev) => ({ ...prev, organizationOptions: data })),
  );
  const { isLoaded: provinceLoaded } = useFetchData(getProvince, (data) =>
    setOptionsState((prev) => ({ ...prev, provinceOptions: data })),
  );

  const fetchData = useCallback(
    (filterValues?: any, paginationValues?: PaginationValues) => {
      setErrorFindingData(false);
      setIsLoaded(false);
      setTimeout(() => {
        getUsers(undefined, filterValues, paginationValues)
          .then((result) => {
            setDataTable(result.content);
            setTotalItems(result.totalItems);
            setIsLoaded(true);
          })
          .catch((error) => {
            console.log("API Error:", error);
            setErrorFindingData(true);
          });
      }, 1000);
    },
    [],
  );

  useEffect(() => {
    const updateCountyOptions = () => {
      setCountyLoaded(false);
      if (filterState.ProvinceId) {
        getCountyById({ ParentId: filterState.ProvinceId.id })
          .then((result) => {
            setOptionsState((prev) => ({
              ...prev,
              countyOptions: result,
            }));
          })
          .finally(() => {
            setCountyLoaded(true);
          });
      } else {
        setCountyLoaded(true);
        setOptionsState((prev) => ({ ...prev, countyOptions: [] }));
      }
    };
    updateCountyOptions();
  }, [filterState.ProvinceId]);

  const handleStateChange = async (id: number, isActive: boolean) => {
    await handleUserStateChange(id, isActive, addToast, () =>
      fetchData(filters),
    );
  };

  const selectComponents = userSelectFilterData(
    optionsState.roleOptions,
    optionsState.organizationOptions,
    optionsState.provinceOptions,
    optionsState.countyOptions,
    optionsState.activeOptions,
    control,
    setFilter,
    (RoleId: any) =>
      setFilterState((prev) => ({
        ...prev,
        RoleId: RoleId,
      })),
    (OrganizationId: any) =>
      setFilterState((prev) => ({
        ...prev,
        OrganizationId: OrganizationId,
      })),
    (ProvinceId: any) =>
      setFilterState((prev) => ({ ...prev, ProvinceId: ProvinceId })),
    (CountyId: any) =>
      setFilterState((prev) => ({ ...prev, CountyId: CountyId })),
    (IsActive: any) =>
      setFilterState((prev) => ({
        ...prev,
        IsActive: IsActive,
      })),
    filterState.ProvinceId,
    provinceLoaded,
    countyLoaded,
    roleLoaded,
    organizationLoaded,
  );

  const filterChildren = (
    <SelectGroup selectComponents={selectComponents} width="230px" />
  );

  const handleAddNewItem = () => {
    navigate("/utilizadores/criar-utilizador");
  };

  const tableConfig: TableConfig = {
    tableName: "Lista de Utilizadores",
    dataTable,
    setDataTable,
    renderTable: (dataTable) => (
      <TableUsers
        dataTable={dataTable}
        isLoaded={isLoaded}
        user={user}
        handleStateChange={handleStateChange}
      />
    ),
    isLoaded,
    errorFindingData,
    searchTitle: "Pesquisar pelo Nome ou Email do utilizador",
  };

  useEffect(() => {
    updateSelectedFilterItemsName(
      filterState,
      filters,
      setSelectedFilterItems,
      filterNames,
    );
  }, [filterNames, filterState, filters]);

  const filtersArray = getFiltersArray(filterNames, filters, setFilter);
  const filterConfig: FilterConfig = {
    filterChildren,
    filters: filtersArray,
    selectedFilterItem: selectedFilterItems,
  };

  const { currentPage, totalPages, handlePageChange, renderContent } =
    useTableRendering(tableConfig, pageSize, totalItems, setIsSearching);

  useEffect(() => {
    isSearching &&
      fetchData(filters, {
        CurrentPage: currentPage,
        PageSize: pageSize,
      });
    setIsSearching(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching]);

  const paginationConfig: PaginationConfig = {
    pageSize,
    currentPage,
    totalPages,
    handlePageChange,
    renderContent,
    setPageSize,
    totalItems,
  };

  const { isOpen, title, buttonTitle, openModal, closeModal } = useCustomModal(
    "",
    "",
  );

  const modalConfig: ModalConfig = {
    isOpen,
    title,
    buttonTitle,
    openModal,
    closeModal,
  };

  const handleDownloadAuditModal = () => {
    openModal("Baixar a auditoria", "Baixar");
  };

  return (
    <SearchTable
      handleAddNewItem={
        user.permissions.includes("CreateUser") ? handleAddNewItem : null
      }
      summaryName={USER_SUMMARY}
      showModal
      searchTableName="Utilizadores"
      createButtonName="Criar utilizador"
      tableConfig={tableConfig}
      filterConfig={filterConfig}
      setIsSearching={setIsSearching}
      paginationConfig={paginationConfig}
      headingExtraElement={
        user.permissions.includes("GetAudit") ? (
          <Button variant="secondary" onClick={handleDownloadAuditModal}>
            Baixar a auditoria
          </Button>
        ) : null
      }
    >
      <ModalDownloadAudit
        control={control}
        errors={errors}
        setValue={setValue}
        modalConfig={modalConfig}
        watch={watch}
      />
    </SearchTable>
  );
};

export default Users;
